import IconNameEnum from "constants/IconNameEnum";
import React, { RefObject } from "react";
import { Form, InputGroup } from "react-bootstrap";
import {
    AllTypeaheadOwnAndInjectedProps,
    Typeahead,
    TypeaheadMenuProps,
    TypeaheadResult,
} from "react-bootstrap-typeahead";
import DropdownOptionModel from "stores/shared/models/DropdownOptionModel";
import FormatUtility from "utilities/FormatUtility";
import { mailRegex } from "utilities/ValidationUtility";
import BaseButton from "./BaseButton";
import BaseIcon from "./BaseIcon";
import InfoIcon from "./InfoIcon";
import RequiredIcon from "./RequiredIcon";

interface IProps {
    controlId: string;
    id?: string;
    label?: string;
    placeholder?: string;
    prepend?: JSX.Element;
    append?: JSX.Element;
    name: string;
    value: string;
    required: boolean;
    infoCode?: string;
    options: DropdownOptionModel[];
    multiple?: boolean;
    disabled?: boolean;
    onFocusHandler?: (name: string, value: any) => void;
    onChangeHandler?: (name: string, value: any) => void;
    onBlurHandler?: (name: string, value: any) => void;
    onInputChangeHandler?: (name: string, value: any) => void;
    isInvalid: boolean;
    error?: string;
    minLength?: number;
    searchInside?: boolean;
    floatingLabel?: boolean;
    customFormat?: "noNumbers" | "noSpecials";
    formatOnDigit?: boolean;
    newOptionAllowed?: boolean;
    newOptionType?: "email";
    hideMenu?: boolean;
    fullWidth?: boolean;
    className?: string;
    sortOptions?: boolean;
}

interface IState {
    onFocused: boolean;
    isMenuOpen: boolean;
    inputText: string;
}

class BaseAutoComplete extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            onFocused: false,
            isMenuOpen: false,
            inputText: "",
        } as IState;
    }

    private hasUnmounted = false;
    private typeaheadRef: RefObject<Typeahead<DropdownOptionModel>> = React.createRef<Typeahead<DropdownOptionModel>>();

    public componentWillUnmount() {
        this.hasUnmounted = true;
    }

    private optionMatches = (option: string, value: string, selected: boolean) => {
        return (selected === false && value.length === 0) || this.getSearched(option, value, selected).length > 0;
    };

    private getSearched = (option: string, value: string, selected: boolean) => {
        let positions = [] as { start: number; end: number }[];
        if (selected === true || (this.props.newOptionAllowed === true && this.props.hideMenu === true)) {
            // check if option equals search text
            if (option.toLowerCase() === value.toLowerCase()) {
                positions.push({ start: 0, end: option.length });
            }
        } else if (value) {
            if (this.props.searchInside === true) {
                // option full text
                let optionOriginal = option.toLowerCase();
                // remaining option words
                let optionWords = option
                    .toLowerCase()
                    .split(" ")
                    .filter((x) => x.length > 0);
                // words searched
                let valueWords = value
                    .toLowerCase()
                    .split(" ")
                    .filter((x) => x.length > 0);
                // search words in option
                for (let i = 0; i < valueWords.length; i++) {
                    let valueWord = valueWords[i];
                    // if option include search word
                    if (optionWords.some((x) => x.includes(valueWord))) {
                        const index = optionWords.findIndex((x) => x.includes(valueWord));
                        const optionWord = optionWords[index];
                        const optionIndex = optionOriginal.indexOf(optionWord);
                        // get word position in option full text
                        const pos = optionOriginal.substring(optionIndex).indexOf(valueWord) + optionIndex;
                        positions.push({ start: pos, end: pos + valueWord.length });
                        // remove searched option word
                        optionWords.splice(index, 1);
                    } else {
                        return [] as { start: number; end: number }[];
                    }
                }
                // order positions
                positions = positions.sort((a, b) => a.start - b.start);
            } else {
                // check if option starts with search text
                if (option.toLowerCase().startsWith(value.toLowerCase())) {
                    positions.push({ start: 0, end: value.length });
                }
            }
        }
        return positions;
    };

    private getValue = () => {
        // from values string to options array
        const { value, multiple } = this.props;
        if (value) {
            let values: string[];
            if (multiple === true) {
                values = value.split(",");
            } else {
                values = [value];
            }
            return this.getSelectedOptions(values);
        }
        return [] as DropdownOptionModel[];
    };

    private formatValue = (value: DropdownOptionModel[]) => {
        // from options array to values string
        let valueFormatted;
        const allowNew = this.props.newOptionAllowed ?? false;
        if (this.props.multiple === true) {
            valueFormatted = value.map((v) => v.value ?? (allowNew ? v.description : "")).join(",");
        } else {
            valueFormatted = value && value[0] ? value[0].value ?? (allowNew ? value[0].description : "") : "";
        }
        return valueFormatted;
    };

    private getSelectedOptions = (values: string[]) => {
        // get selected and new options
        let results = [] as DropdownOptionModel[];
        values.forEach((value) => {
            let option = this.props.options.find((x) => this.optionMatches(value, x.value, true));
            if (this.props.newOptionAllowed === true && option === undefined) {
                option = new DropdownOptionModel({ value: value, description: value });
            }
            if (option) {
                results.push(option);
            }
        });
        return results;
    };

    private onFocusHandler = (e: Event) => {
        // check min length of input text to open menu
        let isMenuOpen = true;
        const { minLength } = this.props;
        if (minLength) {
            isMenuOpen = this.state.inputText.length >= minLength ? true : false;
        }
        if (this.hasUnmounted === false) {
            this.setState({
                onFocused: true,
                isMenuOpen: isMenuOpen,
            });
        }

        if (this.props.onFocusHandler != null) {
            let selectedOption = this.getValue();
            let valueFormatted = this.formatValue(selectedOption);
            this.props.onFocusHandler(this.props.name, valueFormatted);
        }
    };

    private onChangeHandler = (value: DropdownOptionModel[]) => {
        let valuesToRemove = [] as string[];
        if (this.props.multiple && this.props.hideMenu !== true) {
            // remove already selected value
            valuesToRemove = value.map((x) => x.value).filter((item, index, array) => array.indexOf(item) !== index);
        }
        value = value.reduce((unicized, item) => {
            // remove duplicated values
            if (valuesToRemove.includes(item.value) || unicized.map((x) => x.value).includes(item.value)) {
                return unicized;
            } else {
                return [...unicized, item];
            }
        }, [] as DropdownOptionModel[]);
        if (this.props.newOptionAllowed) {
            value.forEach((selected) => {
                // add new options
                if (
                    this.props.options
                        .map((x) => x.description.toLowerCase())
                        .includes(selected.description.toLowerCase()) === false
                ) {
                    this.addAndSelectOption(selected.description, false);
                }
            });
        }
        let valueFormatted = this.formatValue(value);

        if (
            (this.props.multiple !== true && value.length > 0) ||
            (this.props.minLength !== undefined && this.state.inputText.length >= this.props.minLength)
        ) {
            if (this.hasUnmounted === false) {
                this.setState({
                    isMenuOpen: false,
                });
            }
        }

        if (this.props.multiple === true && value.length === 0) {
            this.clearInput();
        }

        if (this.props.onChangeHandler != null) {
            this.props.onChangeHandler(this.props.name, valueFormatted);
        }
    };

    private onInputChangeHandler = (text: string, e: any) => {
        // check min length of input text to open menu
        let isMenuOpen = this.state.isMenuOpen;
        const { minLength } = this.props;
        if (minLength) {
            isMenuOpen = text.length >= minLength ? true : false;
        }
        if (this.hasUnmounted === false) {
            this.setState({
                inputText: text,
                isMenuOpen: isMenuOpen,
            });
        }

        if (this.props.onInputChangeHandler != null) {
            this.props.onInputChangeHandler(this.props.name, text);
        }
    };

    private onBlurHandler = (e: Event) => {
        let isClickOnToggleMenuBtn = false;
        const evt = (e as unknown) as React.FocusEvent<HTMLInputElement>;
        if (
            evt.relatedTarget &&
            (evt.relatedTarget as HTMLElement).classList.contains("typeahead-menu-toggle-button")
        ) {
            isClickOnToggleMenuBtn = true;
        }
        if (this.hasUnmounted === false) {
            this.setState({
                onFocused: false,
                isMenuOpen: isClickOnToggleMenuBtn ? this.state.isMenuOpen : false,
            });
        }

        const value = evt.target.value.toLowerCase();
        this.addAndSelectOption(value, this.props.newOptionAllowed === true);

        if (this.props.onBlurHandler != null) {
            let selectedOption = this.getValue();
            let valueFormatted = this.formatValue(selectedOption);
            this.props.onBlurHandler(this.props.name, valueFormatted);
        }
    };

    private onKeyDown = (e: Event) => {
        let evt = (e as unknown) as React.KeyboardEvent<HTMLInputElement>;
        if (this.props.formatOnDigit) {
            let regex: RegExp;
            switch (this.props.customFormat) {
                case "noNumbers":
                    regex = /[0-9]/g;
                    if ((evt.key.match(regex) || []).length > 0) {
                        evt.preventDefault();
                        evt.stopPropagation();
                    }
                    break;
                case "noSpecials":
                    regex = /[^a-zA-Z0-9àáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,'-]/g;
                    if ((evt.key.match(regex) || []).length > 0) {
                        evt.preventDefault();
                        evt.stopPropagation();
                    }
                    break;
            }
        }

        if (evt.key === "Tab" || evt.key === "Enter") {
            // Tab seleziona l'elemento suggerito (che inizia con)
            // Enter seleziona l'elemento interamente digitato (solo se l'inserimento di nuove opzioni è abilitato)
            const value = evt.currentTarget.value.toLowerCase();
            if (value.length > 0) {
                evt.preventDefault();
                evt.stopPropagation();
                if (evt.key === "Tab" && this.getValue().length === 0) {
                    this.clearInput();
                }
                this.addAndSelectOption(value, true);
            }
        }
    };

    private addAndSelectOption = (value: string, force: boolean) => {
        if (this.props.newOptionAllowed === true && value) {
            value = value.toLowerCase();
            let regex: RegExp | undefined = undefined;
            switch (this.props.newOptionType) {
                case "email":
                    regex = mailRegex;
                    break;
            }
            if (regex === undefined || (value.match(regex) || []).length > 0) {
                this.clearInput();
                if (force) {
                    let selectedOptions = this.getValue();
                    let newOptions = this.getSelectedOptions(value.split(","));
                    newOptions = newOptions.filter((newOption) => selectedOptions.includes(newOption) === false);
                    newOptions.forEach((newOption) => {
                        selectedOptions.push(newOption);
                    });
                    this.onChangeHandler(selectedOptions);
                }
            }
        }
    };

    private onSearch = (option: DropdownOptionModel, props: AllTypeaheadOwnAndInjectedProps<DropdownOptionModel>) => {
        if (props.selected.length) {
            return true;
        }
        return this.optionMatches(option.description, props.text, false);
    };

    private renderMenuItem = (
        option: TypeaheadResult<DropdownOptionModel>,
        props: TypeaheadMenuProps<DropdownOptionModel>,
        index: number
    ) => {
        let optionText = option.description;
        let searchText = props.text?.toLowerCase() ?? "";
        let parts: JSX.Element[] = [];
        let posLast = 0;
        const positions = this.getSearched(optionText, searchText, false);
        positions.forEach((position, posIndex) => {
            if (position.start > posLast) {
                parts.push(
                    <span key={`pre-${index}-${posIndex}`}>{optionText.substring(posLast, position.start)}</span>
                );
            }
            parts.push(
                <mark key={`mark-${index}-${posIndex}`} className="rbt-highlight-text">
                    {optionText.substring(position.start, position.end)}
                </mark>
            );
            posLast = position.end;
        });
        if (posLast < optionText.length) {
            parts.push(<span key={`after-${index}`}>{optionText.substring(posLast)}</span>);
        }
        if (this.props.multiple && this.getValue().some((x) => x.value === option.value)) {
            parts.push(
                <span key={`check-${index}`} className="float-right">
                    <BaseIcon icon={IconNameEnum.Flag} />
                </span>
            );
        }
        return parts;
    };

    private onMenuToggle = (isOpen: boolean) => {
        if (this.typeaheadRef && this.typeaheadRef.current && "focus" in this.typeaheadRef.current) {
            if (isOpen) {
                (this.typeaheadRef.current as any).focus();
            } else {
                if (this.hasUnmounted === false) {
                    this.setState({
                        isMenuOpen: false,
                    });
                }
            }
        }
    };

    private checkIfNewOptionAllowed = () => {
        // check regex to enable adding new option
        if (this.props.newOptionAllowed === true) {
            const value = this.state.inputText;
            let regex: RegExp | undefined = undefined;
            switch (this.props.newOptionType) {
                case "email":
                    regex = mailRegex;
                    break;
            }
            if (regex === undefined || (value.match(regex) || []).length > 0) {
                return true;
            }
        }
        return false;
    };

    private clearInput = () => {
        // clear input text
        if (this.typeaheadRef && this.typeaheadRef.current && "clear" in this.typeaheadRef.current) {
            (this.typeaheadRef.current as any).clear();
            // check min length of input text to open menu
            let isMenuOpen = true;
            const { minLength } = this.props;
            if (minLength && minLength > 0) {
                isMenuOpen = false;
            }
            if (this.hasUnmounted === false) {
                this.setState({
                    inputText: "",
                    isMenuOpen,
                });
            }
        }
    };

    public render(): JSX.Element {
        const {
            controlId,
            id,
            label,
            placeholder,
            prepend,
            append,
            name,
            multiple,
            disabled,
            value,
            options,
            required,
            infoCode,
            isInvalid,
            error,
            floatingLabel,
            minLength,
            hideMenu,
            fullWidth,
            className,
            sortOptions,
        } = this.props;

        const { onFocused, isMenuOpen, inputText } = this.state;

        const floated = onFocused || value.length > 0 || inputText.length > 0;

        return (
            <Form.Group
                className={fullWidth ? ["mb-0 w-100", className!].join(" ") : ["mb-0", className!].join(" ")}
                controlId={controlId}
            >
                {floatingLabel === false && label ? (
                    <Form.Label>
                        {label} {required ? <RequiredIcon /> : null}
                        {infoCode ? <InfoIcon label={label} code={infoCode} /> : null}
                    </Form.Label>
                ) : null}
                <InputGroup
                    className={[
                        "rounded ccq-shadow",
                        floatingLabel !== false ? "floating-label-container" : "",
                        floated ? "floated" : "",
                        prepend ? "with-prepend" : "",
                        append ? "with-append" : "",
                        hideMenu !== true ? "with-menu-button" : "",
                    ].join(" ")}
                >
                    {prepend ? (
                        <InputGroup.Prepend>
                            <InputGroup.Text
                                className={["rounded-left border-right-0", disabled ? "" : "bg-white"].join(" ")}
                            >
                                {prepend}
                            </InputGroup.Text>
                        </InputGroup.Prepend>
                    ) : null}
                    <Typeahead
                        ref={this.typeaheadRef}
                        id={controlId + ".options"}
                        labelKey="description"
                        multiple={multiple}
                        onFocus={this.onFocusHandler}
                        onChange={this.onChangeHandler}
                        onBlur={this.onBlurHandler}
                        onKeyDown={this.onKeyDown}
                        onInputChange={this.onInputChangeHandler}
                        options={sortOptions ? FormatUtility.getSortedArray(options, "description", "A") : options}
                        placeholder={placeholder || " "}
                        // placeholder={floatingLabel === false ? "Selezionare" : placeholder || " "}
                        selected={this.getValue()}
                        caseSensitive={false}
                        ignoreDiacritics={true}
                        emptyLabel={"Nessun risultato trovato"}
                        paginationText={"Carica risultati..."}
                        newSelectionPrefix={"Seleziona: "}
                        maxResults={20}
                        isInvalid={isInvalid}
                        disabled={disabled}
                        clearButton={false}
                        flip={true}
                        filterBy={this.onSearch}
                        renderMenuItemChildren={this.renderMenuItem}
                        open={hideMenu ? false : isMenuOpen}
                        minLength={minLength}
                        allowNew={this.checkIfNewOptionAllowed()}
                    >
                        {floatingLabel !== false && label ? (
                            <Form.Label>
                                {label}
                                {required ? <RequiredIcon /> : null}
                                {infoCode ? <InfoIcon label={label} code={infoCode} /> : null}
                            </Form.Label>
                        ) : null}
                        {hideMenu !== true ? (
                            <div className={`rbt-aux ${disabled ? "rbt-aux-disabled" : ""}`}>
                                <BaseButton
                                    type="button"
                                    color="none"
                                    label=""
                                    icon={isMenuOpen ? IconNameEnum.MenuChiudi : IconNameEnum.MenuApri}
                                    onClickHandler={() => this.onMenuToggle(!isMenuOpen)}
                                    className="typeahead-menu-toggle-button"
                                />
                            </div>
                        ) : null}
                    </Typeahead>
                    <Form.Control
                        type="hidden"
                        className="d-none"
                        id={id}
                        name={name}
                        value={value}
                        isInvalid={isInvalid}
                    />
                    {append ? (
                        <InputGroup.Append>
                            <InputGroup.Text
                                className={["rounded-right border-left-0", disabled ? "" : "bg-white"].join(" ")}
                            >
                                {append}
                            </InputGroup.Text>
                        </InputGroup.Append>
                    ) : null}
                </InputGroup>
                <Form.Control.Feedback type="invalid" className="d-block bg-transparent mt-1">
                    {error}
                </Form.Control.Feedback>
            </Form.Group>
        );
    }
}

export { BaseAutoComplete as Unconnected };
export default BaseAutoComplete;
